/**
 * Alert generator — creates rows in the `alerts` table.
 * Call these from business logic (webform submit, venta pagada, factura vencida, etc.).
 */

import { supabase } from '../supabase';
import { invokeSendPush } from '../push/sendPushNotification';

export interface CreateAlertParams {
  empresaId: string;
  type: string;
  priority: 'critical' | 'important' | 'info';
  title: string;
  message: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  metadata?: Record<string, unknown>;
}

async function createAlert(params: CreateAlertParams): Promise<boolean> {
  try {
    const { error } = await supabase.from('alerts').insert({
      empresa_id: params.empresaId,
      type: params.type,
      priority: params.priority,
      title: params.title,
      message: params.message,
      related_entity_type: params.relatedEntityType || null,
      related_entity_id: params.relatedEntityId || null,
      metadata: params.metadata || null,
      is_read: false,
      is_completed: false,
    });
    if (error) {
      console.error('[AlertGenerator] Error creating alert:', error.message, error.code, error.details, params);
      return false;
    }
    return true;
  } catch (err: unknown) {
    console.error('[AlertGenerator] Unexpected error:', err);
    return false;
  }
}

/** Create a test alert (info) so you can verify the alerts pipeline. */
export async function createTestAlert(empresaId: string): Promise<boolean> {
  return createAlert({
    empresaId,
    type: 'nuevo_lead',
    priority: 'info',
    title: 'Alerta de prueba',
    message: 'Si ves esta alerta, el sistema de notificaciones está funcionando correctamente.',
    metadata: { test: true, createdAt: new Date().toISOString() },
  });
}

/** 1. New lead from form (any webform submission) */
export async function createNewLeadAlert(
  empresaId: string,
  leadId: string,
  clienteNombre: string,
  source?: string
): Promise<boolean> {
  const ok = await createAlert({
    empresaId,
    type: 'nuevo_lead',
    priority: 'critical',
    title: 'Nuevo Lead',
    message: `${clienteNombre} completó un formulario${source ? ` (${source})` : ''}`,
    relatedEntityType: 'lead',
    relatedEntityId: leadId,
  });
  if (ok) {
    const msg = `${clienteNombre} completó un formulario${source ? ` (${source})` : ''}`;
    invokeSendPush({
      empresa_id: empresaId,
      event_type: 'new_lead',
      title: 'Nuevo lead',
      body: msg,
      tipo: 'nuevo_lead',
      notify_empresa_admins: true,
      data: { deep_link: 'clientelo://Clientes', cliente_id: leadId },
    }).then(() => {});
  }
  return ok;
}

/** 2. New order from form (webform with products selected) */
export async function createNewOrderAlert(
  empresaId: string,
  leadId: string,
  clienteNombre: string,
  productCount: number
): Promise<boolean> {
  return createAlert({
    empresaId,
    type: 'nuevo_pedido',
    priority: 'critical',
    title: 'Nuevo Pedido Online',
    message: `${clienteNombre} realizó un pedido con ${productCount} producto${productCount !== 1 ? 's' : ''}`,
    relatedEntityType: 'lead',
    relatedEntityId: leadId,
  });
}

/** Pedido desde chat público / widget (ventas_en_linea + venta) */
export async function createVentaEnLineaAlert(
  empresaId: string,
  clienteId: string,
  clienteNombre: string,
  ventaNumero: string,
  total: number,
  canal: string,
  ventaId?: string
): Promise<boolean> {
  const totalFmt = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(total);
  return createAlert({
    empresaId,
    type: 'venta_en_linea',
    priority: 'critical',
    title: 'Nuevo pedido en línea',
    message: `${clienteNombre} — Pedido ${ventaNumero} · ${totalFmt} · ${canal}`,
    relatedEntityType: 'lead',
    relatedEntityId: clienteId,
    metadata: {
      venta_numero: ventaNumero,
      venta_id: ventaId,
      canal,
    },
  });
}

/** 3. New appointment from form */
export async function createNewAppointmentAlert(
  empresaId: string,
  appointmentId: string,
  clienteNombre: string,
  date: string,
  time: string
): Promise<boolean> {
  return createAlert({
    empresaId,
    type: 'nueva_cita',
    priority: 'critical',
    title: 'Nueva Cita Agendada',
    message: `${clienteNombre} agendó una cita para el ${date} a las ${time}`,
    relatedEntityType: 'appointment',
    relatedEntityId: appointmentId,
  });
}

/** 4. Purchase / sale completed (venta registrada as pagada) */
export async function createPurchaseAlert(
  empresaId: string,
  ventaId: string,
  clienteNombre: string,
  total: number,
  currency: string = 'COP'
): Promise<boolean> {
  const formattedTotal = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(total);

  const message = `${clienteNombre} realizó una compra por ${formattedTotal}`;
  const ok = await createAlert({
    empresaId,
    type: 'compra_realizada',
    priority: 'important',
    title: 'Nueva Compra Registrada',
    message,
    relatedEntityType: 'invoice',
    relatedEntityId: ventaId,
  });
  if (ok) {
    invokeSendPush({
      empresa_id: empresaId,
      event_type: 'new_sale',
      title: 'Nueva venta',
      body: message,
      tipo: 'compra_realizada',
      notify_empresa_admins: true,
      data: { deep_link: 'clientelo://Ventas' },
    }).then(() => {});
  }
  return ok;
}

/** 5. Client in mora (invoice overdue) */
export async function createMoraAlert(
  empresaId: string,
  facturaId: string,
  clienteNombre: string,
  daysOverdue: number,
  amountDue: number,
  currency: string = 'COP'
): Promise<boolean> {
  const formattedAmount = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amountDue);

  const message = `${clienteNombre} tiene una factura vencida hace ${daysOverdue} día${daysOverdue !== 1 ? 's' : ''} por ${formattedAmount}`;
  const ok = await createAlert({
    empresaId,
    type: 'factura_vencida',
    priority: 'critical',
    title: 'Cliente en Mora',
    message,
    relatedEntityType: 'invoice',
    relatedEntityId: facturaId,
    metadata: { daysOverdue, amountDue, currency },
  });
  if (ok) {
    invokeSendPush({
      empresa_id: empresaId,
      event_type: 'mora_alert',
      title: 'Cliente en mora',
      body: message,
      tipo: 'factura_vencida',
      notify_empresa_admins: true,
      data: { deep_link: 'clientelo://Facturas', factura_id: facturaId },
    }).then(() => {});
  }
  return ok;
}

/** 6. Delivery late or not made */
export async function createDeliveryLateAlert(
  empresaId: string,
  facturaId: string,
  clienteNombre: string,
  scheduledDate: string,
  daysLate?: number
): Promise<boolean> {
  const msg =
    daysLate && daysLate > 0
      ? `Entrega a ${clienteNombre} lleva ${daysLate} día${daysLate !== 1 ? 's' : ''} de retraso (programada: ${scheduledDate})`
      : `Entrega a ${clienteNombre} no fue realizada en la fecha programada (${scheduledDate})`;

  const ok = await createAlert({
    empresaId,
    type: 'entrega_tarde',
    priority: 'critical',
    title: 'Entrega Pendiente / Tardía',
    message: msg,
    relatedEntityType: 'invoice',
    relatedEntityId: facturaId,
    metadata: { scheduledDate, daysLate },
  });
  if (ok) {
    invokeSendPush({
      empresa_id: empresaId,
      event_type: 'late_delivery',
      title: 'Entrega pendiente o tardía',
      body: msg,
      tipo: 'entrega_tarde',
      notify_empresa_admins: true,
      data: { deep_link: 'clientelo://Calendar', factura_id: facturaId },
    }).then(() => {});
  }
  return ok;
}

/** 7. Unpaid delivered invoices (daily digest) */
export async function createUnpaidDeliveredDigestAlert(
  empresaId: string,
  invoiceCount: number,
  totalAmount: number,
  currency: string = 'COP',
  options?: { firstFacturaId?: string; numeros?: string[] }
): Promise<boolean> {
  const formattedTotal = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(totalAmount);

  const numeros = options?.numeros ?? [];
  const detail =
    numeros.length > 0
      ? ` (${numeros.slice(0, 5).join(', ')}${numeros.length > 5 ? ` +${numeros.length - 5} más` : ''})`
      : '';

  return createAlert({
    empresaId,
    type: 'factura_no_pagada',
    priority: 'important',
    title: 'Facturas Entregadas Sin Pagar',
    message: `Tienes ${invoiceCount} factura${invoiceCount !== 1 ? 's' : ''} entregada${invoiceCount !== 1 ? 's' : ''} sin pagar por un total de ${formattedTotal}${detail}`,
    relatedEntityType: options?.firstFacturaId ? 'invoice' : undefined,
    relatedEntityId: options?.firstFacturaId ?? undefined,
    metadata: { invoiceCount, totalAmount, currency },
  });
}

/** 8. Low inventory */
export async function createLowInventoryAlert(
  empresaId: string,
  productId: string,
  productName: string,
  currentStock: number
): Promise<boolean> {
  const { data: existing } = await supabase
    .from('alerts')
    .select('id')
    .eq('empresa_id', empresaId)
    .eq('type', 'inventario_bajo')
    .eq('related_entity_id', productId)
    .eq('is_completed', false)
    .limit(1);
  if (existing?.length) return true;

  return createAlert({
    empresaId,
    type: 'inventario_bajo',
    priority: 'important',
    title: 'Inventario Bajo',
    message: `${productName} tiene solo ${currentStock} unidad${currentStock !== 1 ? 'es' : ''} en stock`,
    relatedEntityType: 'product',
    relatedEntityId: productId,
  });
}

/** 9. Uncontacted lead */
export async function createUncontactedLeadAlert(
  empresaId: string,
  leadId: string,
  clienteNombre: string,
  hoursOld: number
): Promise<boolean> {
  return createAlert({
    empresaId,
    type: 'lead_sin_contactar',
    priority: 'important',
    title: 'Lead Sin Contactar',
    message: `${clienteNombre} lleva ${hoursOld} horas sin contacto`,
    relatedEntityType: 'lead',
    relatedEntityId: leadId,
  });
}

/** 10. No-show appointment */
export async function createNoShowAlert(
  empresaId: string,
  appointmentId: string,
  clienteNombre: string,
  date: string,
  time: string
): Promise<boolean> {
  return createAlert({
    empresaId,
    type: 'cita_no_show',
    priority: 'important',
    title: 'Cliente No Asistió',
    message: `${clienteNombre} no asistió a su cita (${date} ${time})`,
    relatedEntityType: 'appointment',
    relatedEntityId: appointmentId,
  });
}
